import React, { memo, useState, useCallback } from 'react';
import {
  Box,
  Card,
  Typography,
  Chip,
  TextField,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Tooltip,
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  MoreVert,
  CallSplit,
  LinkOff,
  Search as SearchIcon,
  Add as AddIcon,
  AccountTree,
  Sync as SyncIcon,
} from '@mui/icons-material';
import {
  QuestionPaperQuestion,
} from '../../services/generateQuestionPaperService';
import MathTextRenderer from '../shared/MathTextRenderer';
import AnswerBadge from '../shared/AnswerBadge';
import MathInput from '../shared/MathInput';
import { formatNumber, formatOptionLabel, NumberingStyle, MCQOptionStyle } from '../../utils/paperNumbering';
import { elegantMenuProps } from '../../utils/menuStyles';
import { toUploadUrl } from '../../utils/uploadUrl';

interface SectionQuestionCardProps {
  question: QuestionPaperQuestion;
  qIndex: number;
  sectionIndex: number;
  isViewMode: boolean;
  /** 1-based display number assigned by the paper layout. Falls back to qIndex+1. */
  questionNumber?: number;
  /** Paper-wide question numbering style. */
  questionNumberStyle?: NumberingStyle;
  /** Paper-wide MCQ option label style — mirrors the PDF/review preview. */
  mcqOptionStyle?: MCQOptionStyle;
  /** Suppress the number chip — used for non-first OR alternatives (which
   * share the source's number and don't get their own label) and for
   * children rendered under a parent (which use the sub-part label instead). */
  hideQuestionNumber?: boolean;
  /** When rendered as a child, this is the (i)/(a)/1. label to display in
   * place of the number chip. */
  childLabel?: string;
  /** When rendering as a child or as a parent, the marks displayed instead of
   * the editable input — parent shows composite sum, child shows own marks. */
  childMarks?: number;
  /** When true, marks input is read-only (displays composite sum). */
  parentMode?: boolean;
  inChoiceGroup?: boolean;
  isParent?: boolean;
  isChild?: boolean;
  onUpdateMarks: (sectionIndex: number, questionIndex: number, marks: number) => void;
  onDelete: (sectionIndex: number, questionIndex: number) => void;
  onEditMCQ: (qIndex: number, sectionIndex: number, question: QuestionPaperQuestion) => void;
  onUpdateQuestionText: (sectionIndex: number, questionIndex: number, text: string) => void;
  onAddOrAlternative?: (sectionIndex: number, questionIndex: number) => void;
  /** Opens the bank picker to add one or more bank questions as OR alternatives. */
  onAddOrAlternativeFromBank?: (sectionIndex: number, questionIndex: number) => void;
  onRemoveFromChoiceGroup?: (sectionIndex: number, questionIndex: number) => void;
  /** Sub-question handlers. */
  onAddSubQuestion?: (sectionIndex: number, questionIndex: number) => void;
  onAddSubQuestionFromBank?: (sectionIndex: number, questionIndex: number) => void;
  onRemoveFromSubGroup?: (sectionIndex: number, questionIndex: number) => void;
  /**
   * Refresh the snapshot on this row from its bank source. Only rendered
   * when the row has a `questionId` (i.e. was sourced from the bank).
   * Parent decides whether the button is momentarily disabled (in-flight)
   * via `isSyncing`.
   */
  onResyncFromBank?: (sectionIndex: number, questionIndex: number) => void;
  isSyncing?: boolean;
}

/**
 * A single question row in the section editor. Renders question text + MCQ
 * options + sub-parts, with an inline marks field and a "…" menu for the
 * choice-group actions (add OR alternative, remove from OR group).
 */
const SectionQuestionCard = memo<SectionQuestionCardProps>(({
  question: q,
  qIndex,
  sectionIndex,
  isViewMode,
  questionNumber,
  questionNumberStyle,
  mcqOptionStyle,
  hideQuestionNumber = false,
  childLabel,
  childMarks,
  parentMode = false,
  inChoiceGroup = false,
  isParent = false,
  isChild = false,
  onUpdateMarks,
  onDelete,
  onEditMCQ,
  onUpdateQuestionText,
  onAddOrAlternative,
  onAddOrAlternativeFromBank,
  onRemoveFromChoiceGroup,
  onAddSubQuestion,
  onAddSubQuestionFromBank,
  onRemoveFromSubGroup,
  onResyncFromBank,
  isSyncing = false,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editingText, setEditingText] = useState('');
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [orMenuAnchor, setOrMenuAnchor] = useState<null | HTMLElement>(null);
  const [subMenuAnchor, setSubMenuAnchor] = useState<null | HTMLElement>(null);

  // Snapshot model: every renderable field lives inline on the row. The
  // `questionId` soft-ref is only a breadcrumb to enable "Sync from bank".
  const questionText = q.questionText;
  const questionType = q.type;
  const subParts: { text: string }[] | undefined = q.subParts;
  const hasSubParts = subParts && subParts.length > 0;
  const usesDialog = questionType === 'MCQ' || questionType === 'Short' || questionType === 'Long';
  const isFromBank = !!q.questionId;
  const hasCustomBadge = !isFromBank;

  const displayNumber = questionNumber ?? (qIndex + 1);
  const numberLabel = questionNumberStyle
    ? formatNumber(displayNumber, questionNumberStyle)
    : `Q${displayNumber}`;

  const handleStartEdit = useCallback(() => {
    if (usesDialog) {
      onEditMCQ(qIndex, sectionIndex, q);
    } else {
      setIsEditing(true);
      setEditingText(questionText || '');
    }
  }, [usesDialog, questionText, onEditMCQ, qIndex, sectionIndex, q]);

  const handleSaveEdit = useCallback(() => {
    onUpdateQuestionText(sectionIndex, qIndex, editingText);
    setIsEditing(false);
  }, [onUpdateQuestionText, sectionIndex, qIndex, editingText]);

  const handleCancelEdit = useCallback(() => setIsEditing(false), []);

  const openMenu = (e: React.MouseEvent<HTMLElement>) => setMenuAnchor(e.currentTarget);
  const closeMenu = () => { setMenuAnchor(null); };

  const hasStructureMenu = !isViewMode && !!(
    onAddOrAlternative || onAddOrAlternativeFromBank || onRemoveFromChoiceGroup ||
    onAddSubQuestion || onAddSubQuestionFromBank || onRemoveFromSubGroup
  );

  return (
    <Card sx={{ mb: 1, p: 1.5 }} variant="outlined">
      <Box display="flex" justifyContent="space-between" alignItems="start">
        <Box flex={1} mr={2}>
          <Box display="flex" gap={0.5} mb={0.5} flexWrap="wrap" alignItems="center">
            {!hideQuestionNumber && (
              <Chip
                label={numberLabel}
                size="small"
                sx={{
                  bgcolor: 'primary.main',
                  color: 'white',
                  fontWeight: 600,
                  height: 24,
                }}
              />
            )}
            {isChild && childLabel && (
              <Chip
                label={childLabel}
                size="small"
                sx={{
                  bgcolor: 'grey.700',
                  color: 'white',
                  fontWeight: 600,
                  height: 22,
                  fontSize: '0.7rem',
                }}
              />
            )}
            {hasCustomBadge && <Chip label="Custom" size="small" color="secondary" sx={{ height: 24 }} />}
            {questionType && <Chip label={questionType} size="small" variant="outlined" sx={{ height: 24 }} />}
            {!isParent && <Chip label={`${q.marks} marks`} size="small" color="info" sx={{ height: 24 }} />}
            {isParent && typeof childMarks === 'number' && (
              <Chip label={`${childMarks} marks (sum)`} size="small" color="info" variant="outlined" sx={{ height: 24 }} />
            )}
            {hasSubParts && <Chip label={`${subParts!.length} parts`} size="small" variant="outlined" color="primary" sx={{ height: 24 }} />}
            {inChoiceGroup && (
              <Tooltip title="Part of a choice group">
                <Chip icon={<CallSplit sx={{ fontSize: 14 }} />} label="OR" size="small" color="warning" sx={{ height: 24 }} />
              </Tooltip>
            )}
            <AnswerBadge answer={q.answer} answerImages={q.answerImages} />
          </Box>

          {isEditing ? (
            <MathInput
              fullWidth
              multiline
              value={editingText}
              onChange={(val) => setEditingText(val)}
              sx={{ mt: 1 }}
              rows={2}
            />
          ) : (
            <>
              <Typography variant="body2" sx={{ mt: 1, fontWeight: 500 }}>
                <MathTextRenderer text={questionText || ''} />
              </Typography>

              {/* Question-stem image attachments — small thumbnails so the
                  builder card stays compact but authors can still see the
                  figures they attached. Read from the row's own snapshot. */}
              {Array.isArray(q.questionImages) && q.questionImages.length > 0 && (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mt: 0.75 }}>
                  {q.questionImages.map((filename: string) => (
                    <Box
                      key={filename}
                      component="img"
                      src={toUploadUrl('question-images', filename)}
                      alt="Question attachment"
                      sx={{
                        maxWidth: 72,
                        maxHeight: 72,
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 0.75,
                        objectFit: 'contain',
                        bgcolor: 'background.paper',
                      }}
                    />
                  ))}
                </Box>
              )}

              {/* MCQ Options */}
              {questionType === 'MCQ' && Array.isArray(q.options) && q.options.length > 0 && (
                <Box sx={{ mt: 0.5 }}>
                  {q.options.map((opt: string, idx: number) => (
                    <Typography key={idx} variant="caption" color="text.secondary" sx={{ display: 'block', fontStyle: 'italic' }}>
                      {formatOptionLabel(idx + 1, mcqOptionStyle || 'upperAlpha')} <MathTextRenderer text={opt} />
                    </Typography>
                  ))}
                </Box>
              )}

              {/* Sub-parts display */}
              {hasSubParts && (
                <Box sx={{ mt: 0.75, ml: 2 }}>
                  {subParts!.map((part, idx) => (
                    <Typography key={idx} variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.6 }}>
                      <Box component="span" sx={{ fontWeight: 600, color: 'primary.main', mr: 0.5 }}>
                        ({idx + 1})
                      </Box>
                      <MathTextRenderer text={part.text} />
                    </Typography>
                  ))}
                </Box>
              )}
            </>
          )}
        </Box>
        <Box display="flex" gap={1} alignItems="center" flexShrink={0}>
          <TextField
            size="small"
            type="number"
            label="Marks"
            value={parentMode && typeof childMarks === 'number' ? childMarks : q.marks}
            onChange={(e) => onUpdateMarks(sectionIndex, qIndex, parseInt(e.target.value) || 0)}
            sx={{ width: 80 }}
            inputProps={{ min: 0 }}
            disabled={isViewMode || parentMode}
            helperText={parentMode ? '(sum)' : undefined}
          />
          {isEditing ? (
            <>
              <IconButton size="small" color="success" onClick={handleSaveEdit}><CheckIcon fontSize="small" /></IconButton>
              <IconButton size="small" color="error"   onClick={handleCancelEdit}><CloseIcon fontSize="small" /></IconButton>
            </>
          ) : (
            !isViewMode && (
              <IconButton size="small" color="primary" onClick={handleStartEdit}><EditIcon fontSize="small" /></IconButton>
            )
          )}
          {/* Sync-from-bank — only visible when the row was sourced from the
              bank (has a soft `questionId` ref). Refreshes the snapshot with
              the latest bank version. Rotating icon while a request is in
              flight; disabled to prevent duplicate clicks. */}
          {!isViewMode && !isEditing && isFromBank && onResyncFromBank && (
            <Tooltip title={isSyncing ? 'Syncing from bank…' : 'Sync from bank — pull the latest version'}>
              <span>
                <IconButton
                  size="small"
                  onClick={() => onResyncFromBank(sectionIndex, qIndex)}
                  disabled={isSyncing}
                  sx={{
                    color: 'text.secondary',
                    '&:hover': { color: 'primary.main', bgcolor: 'action.hover' },
                    '& .MuiSvgIcon-root': isSyncing
                      ? { animation: 'sqcSpin 0.9s linear infinite' }
                      : undefined,
                    '@keyframes sqcSpin': {
                      from: { transform: 'rotate(0deg)' },
                      to:   { transform: 'rotate(360deg)' },
                    },
                  }}
                >
                  <SyncIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          )}
          {!isViewMode && (onAddOrAlternative || onAddOrAlternativeFromBank) && !inChoiceGroup && (
            <>
              <Tooltip title="Add OR alternative — pick from bank or write your own">
                <IconButton
                  size="small"
                  onClick={(e) => setOrMenuAnchor(e.currentTarget)}
                  sx={{ color: 'warning.dark' }}
                >
                  <CallSplit fontSize="small" />
                </IconButton>
              </Tooltip>
              <Menu
                {...elegantMenuProps}
                anchorEl={orMenuAnchor}
                open={Boolean(orMenuAnchor)}
                onClose={() => setOrMenuAnchor(null)}
              >
                {onAddOrAlternativeFromBank && (
                  <MenuItem
                    onClick={() => { onAddOrAlternativeFromBank(sectionIndex, qIndex); setOrMenuAnchor(null); }}
                  >
                    <ListItemIcon><SearchIcon fontSize="small" /></ListItemIcon>
                    From bank
                  </MenuItem>
                )}
                {onAddOrAlternative && (
                  <MenuItem
                    onClick={() => { onAddOrAlternative(sectionIndex, qIndex); setOrMenuAnchor(null); }}
                  >
                    <ListItemIcon><AddIcon fontSize="small" /></ListItemIcon>
                    Custom
                  </MenuItem>
                )}
              </Menu>
            </>
          )}
          {!isViewMode && (onAddSubQuestion || onAddSubQuestionFromBank) && (
            <>
              <Tooltip title="Add sub-question — pick from bank or write your own">
                <IconButton
                  size="small"
                  onClick={(e) => setSubMenuAnchor(e.currentTarget)}
                  sx={{ color: 'primary.dark' }}
                >
                  <AccountTree fontSize="small" />
                </IconButton>
              </Tooltip>
              <Menu
                {...elegantMenuProps}
                anchorEl={subMenuAnchor}
                open={Boolean(subMenuAnchor)}
                onClose={() => setSubMenuAnchor(null)}
              >
                {onAddSubQuestionFromBank && (
                  <MenuItem
                    onClick={() => { onAddSubQuestionFromBank(sectionIndex, qIndex); setSubMenuAnchor(null); }}
                  >
                    <ListItemIcon><SearchIcon fontSize="small" /></ListItemIcon>
                    From bank
                  </MenuItem>
                )}
                {onAddSubQuestion && (
                  <MenuItem
                    onClick={() => { onAddSubQuestion(sectionIndex, qIndex); setSubMenuAnchor(null); }}
                  >
                    <ListItemIcon><AddIcon fontSize="small" /></ListItemIcon>
                    Custom
                  </MenuItem>
                )}
              </Menu>
            </>
          )}
          {!isViewMode && (
            <IconButton size="small" color="error" onClick={() => onDelete(sectionIndex, qIndex)}>
              <DeleteIcon fontSize="small" />
            </IconButton>
          )}
          {hasStructureMenu && (
            <>
              <Tooltip title="More actions">
                <IconButton size="small" onClick={openMenu}><MoreVert fontSize="small" /></IconButton>
              </Tooltip>
              <Menu
                {...elegantMenuProps}
                anchorEl={menuAnchor}
                open={Boolean(menuAnchor)}
                onClose={closeMenu}
              >
                {onAddOrAlternativeFromBank && (
                  <MenuItem
                    onClick={() => { onAddOrAlternativeFromBank(sectionIndex, qIndex); closeMenu(); }}
                  >
                    <ListItemIcon><SearchIcon fontSize="small" /></ListItemIcon>
                    <ListItemText primary="Add OR alternative — from bank" />
                  </MenuItem>
                )}
                {onAddOrAlternative && (
                  <MenuItem
                    onClick={() => { onAddOrAlternative(sectionIndex, qIndex); closeMenu(); }}
                  >
                    <ListItemIcon><CallSplit fontSize="small" /></ListItemIcon>
                    <ListItemText primary="Add OR alternative — custom" />
                  </MenuItem>
                )}
                {onRemoveFromChoiceGroup && inChoiceGroup && (
                  <MenuItem
                    onClick={() => { onRemoveFromChoiceGroup(sectionIndex, qIndex); closeMenu(); }}
                  >
                    <ListItemIcon><LinkOff fontSize="small" /></ListItemIcon>
                    <ListItemText primary="Remove from OR group" />
                  </MenuItem>
                )}
                {onAddSubQuestionFromBank && (
                  <MenuItem
                    onClick={() => { onAddSubQuestionFromBank(sectionIndex, qIndex); closeMenu(); }}
                  >
                    <ListItemIcon><SearchIcon fontSize="small" /></ListItemIcon>
                    <ListItemText primary="Add sub-question — from bank" />
                  </MenuItem>
                )}
                {onAddSubQuestion && (
                  <MenuItem
                    onClick={() => { onAddSubQuestion(sectionIndex, qIndex); closeMenu(); }}
                  >
                    <ListItemIcon><AccountTree fontSize="small" /></ListItemIcon>
                    <ListItemText primary="Add sub-question — custom" />
                  </MenuItem>
                )}
                {onRemoveFromSubGroup && (isParent || isChild) && (
                  <MenuItem
                    onClick={() => { onRemoveFromSubGroup(sectionIndex, qIndex); closeMenu(); }}
                  >
                    <ListItemIcon><LinkOff fontSize="small" /></ListItemIcon>
                    <ListItemText primary={isParent ? 'Disband sub-question group' : 'Remove from parent'} />
                  </MenuItem>
                )}
              </Menu>
            </>
          )}
        </Box>
      </Box>
    </Card>
  );
});

SectionQuestionCard.displayName = 'SectionQuestionCard';

export default SectionQuestionCard;
